/**
 * OtherGamesSection — sibling titles, each its own Drop.
 *
 * Built to `devourplay-anchor/Drop-In/team-drops/publisher-drop.html`.
 *
 * This is how a title Drop expresses its franchise without trying to BE the
 * franchise. A franchise like Call of Duty has ~20 titles: a page can neither
 * hero one of them nor list them all, and every attempt to do so produced mush.
 * A Drop has one roster, one audience and one calendar — a title has all three,
 * a franchise has twenty of each. So the title Drop links across to its
 * siblings, and each sibling carries its own creator team.
 *
 * Each card DOES carry art, and that reversed an earlier decision here.
 *
 * The rule used to be "no key art on these cards", on the reasoning that we
 * only hold art for the title this Drop is about and borrowing it for a sibling
 * would misrepresent the game. That reasoning stopped applying once every Drop
 * carried its OWN three images: the art on a sibling card is that sibling's
 * hero, resolved from its own record, so nothing is borrowed and nothing is
 * misrepresented. A card with no art of its own still falls back to a mark, a
 * name and a status, which is what the old rule produced for every card.
 */

import React from "react";
/* Shared quoting helper — a signed CDN URL must not break out of `url()`. */
import {safeCssUrl} from "./play/safeHref";
import "./OtherGamesSection.scss";
import {formatCompactNumber} from "@/utils/formatCompactNumber";

export interface SiblingTitle {
    name: string;
    /**
     * The game's own art — key art, logo, or its Drop's identity image,
     * whichever the server could resolve.
     *
     * Absent is normal and fine: a sibling is an associated game, and one with
     * no art configured falls back to the text mark below rather than to a
     * broken image. That fallback is why the rail worked at all before this
     * existed — every card was using it.
     */
    imageUrl?: string;
    /**
     * The title's logo lockup — the card's mark, laid over the art.
     *
     * A game is recognised by its logo, not by two letters. Falls back to the
     * text mark below when a title has none.
     */
    logoUrl?: string;
    /**
     * Square icon. Rendered in the small mark, NOT the wide logo frame.
     *
     * A sibling supplies its icon, and passing that as `logoUrl` put a square
     * image in a 96x44 box — the clipped "CALL" over the Call of Duty card. The
     * two are different shapes and the card has to be told which it got.
     */
    iconUrl?: string;
    /** Short mark — "BO7", "WZ". Derived from the title when absent. */
    mark?: string;
    /** "Season 05", "free to play" — whatever is true of it right now. */
    status?: string;
    /** Live players, when known. */
    playing?: number;
    href?: string;
}

interface OtherGamesSectionProps {
    titles: SiblingTitle[];
    /**
     * The parent Drop's name — "Other Call of Duty Games". Comes from the
     * parent itself, so it stays right when the franchise is renamed and
     * cannot drift the way a name copied onto each title would.
     */
    parentName?: string;
    /** The parent's own page, when it has one. A parent may exist only to group its children. */
    parentHref?: string;
}

function markFor(title: SiblingTitle): string {
    if (title.mark) return title.mark;
    return title.name.split(/\s+/).map(w => w[0])
        .join("")
        .slice(0, 4)
        .toUpperCase();
}


export function OtherGamesSection({titles, parentName, parentHref}: OtherGamesSectionProps): React.ReactElement | null {
    if (!titles.length) return null;

    /*
     * Always "Other Games".
     *
     * It read "Other Activision Games", which names the publisher to someone
     * already on that publisher's page — and grows to "Other Call of Duty
     * Games" the moment the parent is a franchise. The rail sits under a page
     * that has already said whose it is; the heading only has to say what the
     * cards are.
     */
    const heading = "Other Games";

    return (
        <section className="other-games" aria-label={heading}>
            <div className="other-games__head">
                <h3 className="other-games__title">
                    {parentHref
                        ? <a className="other-games__title-link" href={parentHref}>{heading}</a>
                        : heading
                    }
                </h3>
                {/*
                  * No note here.
                  *
                  * It read "each one its own Drop, with its own creator team".
                  * The second half was false — these siblings return
                  * `streamingTeamId: null`, so it asserted a roster none of them
                  * had. The first half was true but said nothing a visitor
                  * needed: "Drop" is our internal word for the page they are
                  * already looking at, and the cards say "Open Drop" anyway.
                  */}
            </div>

            <div className="other-games__grid">
                {titles.map(t => {
                    const Card = t.href ? "a" : "div";
                    return (
                        <Card key={t.name} className="og-card" {...(t.href ? {href: t.href} : {})}>
                            {/*
                              * Key art is the card's BACKGROUND, not a thumbnail.
                              *
                              * It was a 68px box beside the name, which is the
                              * one thing a piece of key art is no good at —
                              * it is composed to be seen wide. Behind the card
                              * it does the job it was made for, and the ICON
                              * sits on top as the mark — a wide logo only if no
                              * icon was set.
                              */}
                            {t.imageUrl &&
                                <span
                                    className="og-card__art"
                                    style={{backgroundImage: safeCssUrl(t.imageUrl)}}
                                    aria-hidden="true"
                                />
                            }
                            <span className="og-card__scrim" aria-hidden="true" />

                            {/* Icon first, then a wide logo, then initials — never an
                                empty box or a broken image. The two images are
                                different shapes and get different frames. */}
                            <span className={`og-card__mark${t.logoUrl && !t.iconUrl ? " og-card__mark--logo" : ""}`}>
                                {t.iconUrl
                                    ? <img src={t.iconUrl} alt="" loading="lazy" />
                                    : t.logoUrl
                                        ? <img src={t.logoUrl} alt="" loading="lazy" />
                                        : markFor(t)}
                            </span>
                            <span className="og-card__name">{t.name}</span>
                            <span className="og-card__status">
                                {t.playing !== undefined && <b>● {formatCompactNumber(t.playing)} playing</b>}
                                {t.playing !== undefined && t.status ? " · " : ""}
                                {t.status}
                            </span>
                            {/* "Open", not "Open Drop" — "Drop" is our word for the page the
                                visitor is already on, so it explains nothing. */}
                            {t.href && <span className="og-card__go">Open →</span>}
                        </Card>
                    );
                })}
            </div>
        </section>
    );
}
